/**
 * RWBR — THE BOOTH: register.
 *
 * NARRATION_MODEL §1 and §3. The single most valuable piece of narrative
 * texture in this project costs nothing, because it is already in the track
 * data and nobody was reading it.
 *
 * A TURN IS TEN MILES, not ten laps (RB/p.ii — 250 miles = 25 turns, 500 = 50,
 * 600 = 60, exactly). Miles are fixed; laps are not. So how many laps a turn
 * contains is a property of the TRACK:
 *
 *   Martinsville  0.526 mi → 19.0 laps a turn
 *   Charlotte     1.5   mi →  6.7
 *   Talladega     2.66  mi →  3.8
 *
 * The same die roll is therefore a completely different story depending on
 * where you are. A turn at Martinsville is nineteen laps of beating and banging
 * and brake heat. A turn at Talladega is under four laps of a freight train at
 * two hundred miles an hour where nothing happens and then everything does.
 *
 * This module turns that arithmetic into the booth's VOICE. It is the reason
 * the same scripted line does not sound the same at two different tracks, and
 * therefore a large part of the answer to §8's repetition problem — variety
 * that comes from the world rather than from a thesaurus.
 */

import type { TrackCard } from '../turn.ts';

export const CITE_TURN_IS_TEN_MILES = 'RB/p.ii — a turn is ten miles: 250 = 25 turns, 500 = 50, 600 = 60';
export const CITE_REGISTER = 'NARRATION_MODEL §3 — register is set by laps per turn';

/** A turn is ten miles. Everything here follows from that one fact. */
export const MILES_PER_TURN = 10;

export type Register = 'short' | 'intermediate' | 'speedway';

/**
 * Laps in one turn at this track, or `null` where the card prints no lap
 * length.
 *
 * `null` is returned rather than a guessed default. A track whose length we do
 * not know must not be narrated as though we do — the booth falls back to the
 * neutral register and says nothing about laps at all.
 */
export function lapsPerTurn(track: Pick<TrackCard, 'lengthMiles'>): number | null {
  const miles = track.lengthMiles;
  if (typeof miles !== 'number' || !Number.isFinite(miles) || miles <= 0) return null;
  return MILES_PER_TURN / miles;
}

/**
 * NARRATION_MODEL §3, verbatim thresholds:
 *   ≥ 15 laps/turn → short track
 *   6–14           → intermediate
 *   ≤ 5            → superspeedway / road
 *
 * An unknown lap length gets `intermediate`, which is the register that claims
 * the least.
 */
export function registerFor(laps: number | null): Register {
  if (laps === null) return 'intermediate';
  if (laps >= 15) return 'short';
  if (laps <= 5) return 'speedway';
  return 'intermediate';
}

export const registerOf = (track: Pick<TrackCard, 'lengthMiles'>): Register =>
  registerFor(lapsPerTurn(track));

/**
 * How many laps a stretch of `turns` turns covers, rounded to a whole lap.
 *
 * Rounded because "12.4 laps" is not how anybody in a broadcast booth speaks,
 * and `null` stays `null` — a stretch whose length is unknown produces no
 * claim about its length rather than a plausible-sounding invention.
 */
export function lapsOver(track: Pick<TrackCard, 'lengthMiles'>, turns: number): number | null {
  const per = lapsPerTurn(track);
  if (per === null) return null;
  return Math.round(per * turns);
}

/** Miles covered by a stretch. Always known — a turn is ten miles everywhere. */
export const milesOver = (turns: number): number => turns * MILES_PER_TURN;

/**
 * The words each register reaches for. Vocabulary, not sentences — the line
 * builders compose with these so that a Martinsville call and a Talladega call
 * differ in texture rather than in template.
 */
export interface RegisterVoice {
  register: Register;
  /** What a stretch of racing is called here. */
  stretch: string[];
  /** What wears a car and a driver down here. */
  attrition: string[];
  /** How a pass reads here. */
  passing: string[];
  /** What the field looks like here. */
  field: string[];
}

/*
  ═════════════════════════════════════════════════════════════════════════════
  D-EZ-74 — THE VOCABULARY, ROUGHLY TRIPLED.

  These four pools are the multiplier under everything the deterministic booth
  says: `lines.ts` composes `<subject> <passing> <object>`, `<attrition> through
  <stretch>`, `<field>, and <attrition> starting to show`, and so on. Three
  entries in `stretch` is three ways to describe a green-flag run for a whole
  season of Martinsvilles.

  ⚠ NO SUPERLATIVES IN A PHRASE POOL. The first draft of this block shipped
  'the longest green flag of the day', 'the best part of a stint' and 'a set of
  tires past their best', and `repetition.test.ts` caught all three inside a
  minute: §8.5 permits a superlative only where the LEDGER supports it, and a
  superlative baked into a phrase pool is a claim the booth makes at random
  about a race it has not checked. "The longest green flag of the day" is a
  measurable assertion. An idiom is not an exemption.

  ⚠ THIS HAD TO MOVE BEFORE THE BEAT COUNT COULD. Doubling how often the booth
  speaks against the old pools does not produce twice the commentary; it
  produces the same commentary, refused twice as often by the five-gram gate,
  falling back twice as often to the engine's plainer sentence. Widening here is
  what actually buys the extra beats.

  Composition rules that are not obvious and were each learned the hard way:
   • `passing` entries are TRANSITIVE AND BARE. They are dropped between two
     names, so an entry carrying its own pronoun gave "Rountree leaned on him
     Chase Coffey" — a thing that passes every test and destroys the illusion on
     sight.
   • `attrition` and `field` are NOUN PHRASES, never clauses; they are used as
     "<attrition> starting to show" and "<field>, and <attrition> telling".
   • `stretch` is a SPAN, used as "through <stretch>".
  ═════════════════════════════════════════════════════════════════════════════
*/
export const VOICES: Readonly<Record<Register, RegisterVoice>> = {
  short: {
    register: 'short',
    stretch: [
      'a long green run',
      'a grinding stretch',
      'a hard twenty laps',
      'a half-hour of this',
      'a run nobody enjoyed',
      'a green flag that would not end',
      'a stretch that felt twice as long',
      'lap after lap of the same thing',
    ],
    attrition: [
      'brake heat',
      'tire wear',
      'sheet metal',
      'frayed tempers',
      'lapped traffic',
      'a bent fender',
      'the concrete',
      'worn-out patience',
      'a car that will not turn',
      'the wall on the exit',
    ],
    passing: [
      'moved out of the way',
      'got under',
      'wore down',
      'leaned on',
      'moved aside',
      'ran the bottom on',
      'got the fender to',
      'took the corner from',
      'rooted out',
      'finally got clear of',
    ],
    field: [
      'a tight, angry pack',
      'a train of cars',
      'traffic everywhere',
      'a queue with nowhere to go',
      'cars three abreast where there is room for two',
      'a field with its elbows out',
    ],
  },
  intermediate: {
    register: 'intermediate',
    stretch: [
      'a stint',
      'a long run',
      'a green-flag run',
      'most of a fuel run',
      'a quiet twenty-five laps',
      'the middle of the race',
      'a long, honest run',
      'most of a green-flag stint',
    ],
    attrition: [
      'tire fall-off',
      'a loose racecar',
      'the fuel window',
      'track position',
      'a tight entry',
      'the long run speed',
      'a car that came to him',
      'the second groove',
      'clean air',
      'a set of tires that has had enough',
    ],
    passing: [
      'cleared',
      'drove around',
      'got the run on',
      'worked past',
      'showed a nose to',
      'built on and passed',
      'ran down and cleared',
      'came off the corner ahead of',
      'took the line from',
      'made the pass on',
    ],
    field: [
      'the field strung out',
      'cars in packets',
      'a spread-out field',
      'clear track between them',
      'a race that has come apart',
      'nobody within a straightaway',
    ],
  },
  speedway: {
    register: 'speedway',
    stretch: [
      'a handful of laps',
      'barely four laps',
      'the space of a lap or two',
      'no time at all',
      'half a dozen laps',
      'the length of the back straight',
      'one trip around',
      'a couple of circuits',
    ],
    attrition: [
      'the draft',
      'the pack',
      'a push from behind',
      'nowhere to go',
      'a bad run',
      'the air off the front',
      'a lane that stalls',
      'forty cars of momentum',
      'a shove that came too late',
      'the wrong line',
    ],
    passing: [
      'got the run on',
      'went three-wide around',
      'got a shove and cleared',
      'came with a head of steam past',
      'got the bumper to',
      'towed past',
      'pulled out and cleared',
      'took the high lane from',
      'got the push and beat',
      'timed the run on',
    ],
    field: [
      'a freight train',
      'the whole field nose to tail',
      'a pack forty cars deep',
      'two lanes running as one',
      'a wall of cars',
      'everybody within a second of everybody',
    ],
  },
};

export const voiceFor = (register: Register): RegisterVoice => VOICES[register];

/**
 * "21st", not "21th".
 *
 * Lives here so there is exactly one of these in the booth. The researcher was
 * building its own with a hardcoded "th" and put "he started 21th" on screen —
 * a line that passes every repetition gate there is and still tells the reader,
 * instantly, that nobody is home.
 */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/** "sixth" up to tenth, then "21st" — the way a booth says a running position. */
const ORDINAL_WORDS = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];
export function ordinalWord(n: number): string {
  return n >= 1 && n < ORDINAL_WORDS.length ? ORDINAL_WORDS[n] : ordinal(n);
}

/**
 * How a booth calls a climb, in racing language rather than board-game
 * language ("gains 23 places"): the same two facts — where he was, where he
 * is — said the way a broadcast says them. A big run "storms"; a short one
 * "is up to".
 */
export function climbLine(name: string, from: number, to: number): string {
  return from - to >= 8
    ? `${name} storms from ${ordinal(from)} to ${ordinal(to)}`
    : `${name} is up to ${ordinal(to)}, from ${ordinal(from)}`;
}

/**
 * A plain description of the stretch just run, for the booth's OPEN beat.
 *
 * Returns `null` when the lap length is unknown, so the caller omits the clause
 * entirely rather than printing a sentence with a hole in it.
 */
export function stretchPhrase(track: Pick<TrackCard, 'lengthMiles'>, turns: number): string | null {
  const laps = lapsOver(track, turns);
  if (laps === null) return null;
  if (turns <= 0) return null;
  return `${laps} lap${laps === 1 ? '' : 's'}`;
}
